import { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../api/client';
import { ApiError, Category } from '../api/types';
import { useBudget } from '../store/BudgetContext';
import { useToast } from '../components/Toast';
import { Icon, categoryIconName } from '../components/Icon';
import { PlanPadKeys, usePlanDraft } from '../components/PlanPad';
import { fmtPlanNum } from '../utils/plan-pad';
import { planDailyLimit, planEnvelopeSum, planPerDay } from '../utils/plan-limit';
import { plural } from '../utils/plural';
import { nextSortToEnd } from '../utils/category-order';
import { theme } from '../theme';

const { ink, sub, subSoft, paper, red, green, line } = theme.colors;

// ---------------------------------------------------------------------------
// Тики прогресса
// ---------------------------------------------------------------------------

function ProgressTicks({ total, current }: { total: number; current: number }) {
  return (
    <div style={{ display: 'inline-flex', alignItems: 'flex-end', gap: 3 }}>
      {Array.from({ length: total }, (_, i) => {
        const done = i < current;
        const active = i === current;
        return (
          <span
            key={i}
            style={{
              width: 3,
              height: active ? 14 : 10,
              background: done || active ? ink : 'transparent',
              borderLeft: done || active ? undefined : `2px solid ${subSoft}`,
              display: 'inline-block',
            }}
          />
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Шаг прохода
// ---------------------------------------------------------------------------

function CheckStep({
  category,
  idx,
  total,
  onNext,
  onSkip,
}: {
  category: Category;
  idx: number;
  total: number;
  onNext: (newValue: number) => void;
  onSkip: () => void;
}) {
  const { value, press, erase } = usePlanDraft(category.plan_month);
  const { viewData } = useBudget();
  const ops = viewData?.operations ?? [];

  // Число операций категории за последние 180 дней (useMemo на весь viewData.operations)
  const recentCount = useMemo(() => {
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - 180);
    const cutoffStr = cutoff.toISOString().slice(0, 10);
    return ops.filter((o) => o.category === category.name && o.date >= cutoffStr).length;
  }, [ops, category.name]);

  // Курсив-подстрока: «повседневная» | «нерегулярная — конверт»; «в квестах»; «N операций за полгода»
  const subParts: string[] = [];
  subParts.push(category.is_daily ? 'повседневная' : 'нерегулярная — конверт');
  if (category.is_hot) subParts.push('в квестах');
  subParts.push(`${recentCount} ${plural(recentCount, ['операция', 'операции', 'операций'])} за полгода`);

  return (
    <div>
      {/* Шапка: «сверка планов · idx+1 из N» + тики прогресса */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
        }}
      >
        <span
          style={{
            textTransform: 'uppercase',
            letterSpacing: '.18em',
            fontSize: 12,
            color: sub,
          }}
        >
          сверка планов · {idx + 1} из {total}
        </span>
        <ProgressTicks total={total} current={idx} />
      </div>

      {/* Карточка категории */}
      <div style={{ marginTop: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Icon name={categoryIconName(category)} size={20} color={ink} />
          <span
            style={{
              fontFamily: theme.fontDisplay,
              fontWeight: 420,
              fontVariationSettings: "'opsz' 144, 'WONK' 1",
              fontSize: 26,
              color: ink,
            }}
          >
            {category.name}
          </span>
        </div>
        <div
          style={{
            fontSize: 12,
            color: sub,
            fontStyle: 'italic',
            marginTop: 2,
          }}
        >
          {subParts.join(' · ')}
        </div>
      </div>

      {/* Текущий план */}
      <div
        style={{
          fontFamily: theme.fontDisplay,
          fontVariationSettings: "'opsz' 144, 'WONK' 0",
          fontVariantNumeric: 'tabular-nums',
          fontSize: 40,
          marginTop: 16,
          color: ink,
        }}
      >
        {fmtPlanNum(value)}
        <span style={{ fontSize: 17, color: sub }}>
          {category.is_daily
            ? ` ₽/мес · ≈${fmtPlanNum(planPerDay(value))} ₽/день`
            : ' ₽/мес'}
        </span>
      </div>

      {/* Клавиатура (PlanPadKeys зашивает marginTop 12, добавляем +2) */}
      <div style={{ marginTop: 2 }}>
        <PlanPadKeys size="check" onKey={press} onErase={erase} />
      </div>

      {/* Кнопки */}
      <div style={{ marginTop: 14, display: 'flex', gap: 12, alignItems: 'center' }}>
        <button
          onClick={() => onNext(value)}
          style={{
            flex: 1,
            padding: '13px 0',
            background: ink,
            color: paper,
            border: 'none',
            borderRadius: theme.radius.sm,
            fontFamily: theme.fontDisplay,
            fontSize: 16,
            cursor: 'pointer',
          }}
        >
          Дальше
        </button>
        <button
          onClick={onSkip}
          style={{
            background: 'transparent',
            border: 'none',
            color: red,
            fontSize: 13,
            borderBottom: `1px solid ${red}`,
            padding: '0 2px 1px',
            cursor: 'pointer',
            fontFamily: theme.font,
          }}
        >
          не нужна
        </button>
      </div>

      {/* Подсказка */}
      <div
        style={{
          fontSize: 12,
          color: sub,
          fontStyle: 'italic',
          marginTop: 12,
        }}
      >
        «не нужна» — план 0 и вниз списка (или удаление, если операций нет)
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Финал
// ---------------------------------------------------------------------------

function CheckFinale({
  cats,
  onDone,
  onRestart,
}: {
  cats: Category[];
  onDone: () => void;
  onRestart: () => void;
}) {
  return (
    <div
      className="rise"
      style={{
        border: `1px solid ${green}`,
        borderRadius: theme.radius.sm,
        padding: '18px 16px',
        position: 'relative',
        marginTop: 8,
      }}
    >
      {/* Штамп «сверено» */}
      <div
        style={{
          position: 'absolute',
          top: 14,
          right: 14,
          transform: 'rotate(-7deg)',
          border: `1.5px solid ${green}`,
          color: green,
          fontSize: 10,
          letterSpacing: '.16em',
          textTransform: 'uppercase',
          padding: '2px 7px',
        }}
      >
        сверено
      </div>

      {/* Заголовок */}
      <div
        style={{
          fontFamily: theme.fontDisplay,
          fontWeight: 420,
          fontVariationSettings: "'opsz' 144, 'WONK' 1",
          fontSize: 24,
          color: ink,
        }}
      >
        Готово
      </div>

      {/* Грид итогов */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr auto',
          gap: '8px 12px',
          marginTop: 12,
          fontSize: 14,
        }}
      >
        <span style={{ color: sub }}>дневной лимит</span>
        <span
          style={{
            fontVariantNumeric: 'tabular-nums',
            textAlign: 'right',
            color: ink,
          }}
        >
          ≈{fmtPlanNum(planDailyLimit(cats))} ₽/день
        </span>
        <span style={{ color: sub }}>конверты месяца</span>
        <span
          style={{
            fontVariantNumeric: 'tabular-nums',
            textAlign: 'right',
            color: ink,
          }}
        >
          {fmtPlanNum(planEnvelopeSum(cats))} ₽
        </span>
      </div>

      {/* Кнопки */}
      <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
        <button
          onClick={onDone}
          style={{
            flex: 1,
            padding: '11px 0',
            background: ink,
            color: paper,
            border: 'none',
            borderRadius: theme.radius.sm,
            fontFamily: theme.fontDisplay,
            fontSize: 15,
            cursor: 'pointer',
          }}
        >
          К списку
        </button>
        <button
          onClick={onRestart}
          style={{
            padding: '11px 14px',
            background: 'transparent',
            color: sub,
            border: `1px solid ${line}`,
            borderRadius: theme.radius.sm,
            fontSize: 13,
            cursor: 'pointer',
            fontFamily: theme.font,
          }}
        >
          заново
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Основной компонент CheckTab
// ---------------------------------------------------------------------------

export function CheckTab({ onDone }: { onDone: () => void }) {
  const { viewData, updateCategory, refresh } = useBudget();
  const toast = useToast();

  const cats = viewData?.categories ?? [];

  // Снапшот имён расходных категорий при монтировании, отсортированный по sort_order
  const snapshotRef = useRef<string[]>(
    cats
      .filter((c) => c.type === 'Расход')
      .sort((a, b) => a.sort_order - b.sort_order)
      .map((c) => c.name),
  );

  const [idx, setIdx] = useState(0);
  const [done, setDone] = useState(false);

  // Полный счётчик операций для каждой категории (для «не нужна»)
  const totalOpCountMap = useMemo<Map<string, number>>(() => {
    const ops = viewData?.operations ?? [];
    const m = new Map<string, number>();
    for (const o of ops) {
      m.set(o.category, (m.get(o.category) ?? 0) + 1);
    }
    return m;
  }, [viewData?.operations]);

  const snapshot = snapshotRef.current;
  const N = snapshot.length;

  // Вычисляем живую категорию текущего шага (до хуков, чтобы передать в useEffect)
  const currentName = !done && N > 0 ? snapshot[idx] : null;
  const currentFound = currentName ? cats.find((c) => c.name === currentName) : null;

  // Сохраняем флаг завершения сверки (идемпотентно, тихо)
  useEffect(() => {
    if (done) api.setSetting('categories_reviewed', '1').catch(() => {});
  }, [done]);

  // Пропуск категорий, удалённых извне: если текущий шаг не найден в cats —
  // сдвигаем idx к следующему живому или переходим в done.
  useEffect(() => {
    if (done || N === 0 || currentFound) return;
    // Текущий шаг удалён — найти следующий живой
    let nextIdx = N; // по умолчанию финал
    for (let i = idx + 1; i < N; i++) {
      if (cats.find((c) => c.name === snapshot[i])) {
        nextIdx = i;
        break;
      }
    }
    if (nextIdx >= N) {
      setDone(true);
    } else {
      setIdx(nextIdx);
    }
  }, [idx, cats]); // eslint-disable-line react-hooks/exhaustive-deps

  function advance() {
    const next = idx + 1;
    if (next >= N) {
      setDone(true);
    } else {
      setIdx(next);
    }
  }

  function handleRestart() {
    // Новый снапшот, сброс индекса
    snapshotRef.current = cats
      .filter((c) => c.type === 'Расход')
      .sort((a, b) => a.sort_order - b.sort_order)
      .map((c) => c.name);
    setIdx(0);
    setDone(false);
  }

  // Финал если нет расходных категорий или проход завершён
  if (N === 0 || done) {
    return (
      <CheckFinale
        cats={cats}
        onDone={onDone}
        onRestart={handleRestart}
      />
    );
  }

  // Пока эффект ещё не отработал (категория удалена, ждём следующего рендера)
  if (!currentFound) return null;

  // Здесь category гарантированно существует
  const category: Category = currentFound;

  function handleNext(newValue: number) {
    if (newValue !== category.plan_month) {
      updateCategory({ name: category.name, plan_month: newValue }).catch((e) => {
        if (e instanceof ApiError) toast.show(e.message, 'error');
        else toast.show('Ошибка при сохранении', 'error');
      });
    }
    advance();
  }

  async function handleSkip() {
    const ops = totalOpCountMap.get(category.name) ?? 0;
    if (ops === 0) {
      try {
        await api.deleteCategory(category.name);
        await refresh();
        toast.reward({
          title: `Удалена «${category.name}»`,
          coin: false,
          sub: 'операций не было',
        });
      } catch (e) {
        if (e instanceof ApiError) toast.show(e.message, 'error');
        else toast.show('Ошибка при удалении', 'error');
      }
    } else {
      updateCategory({
        name: category.name,
        plan_month: 0,
        sort_order: nextSortToEnd(cats, 'Расход'),
      }).catch((e) => {
        if (e instanceof ApiError) toast.show(e.message, 'error');
        else toast.show('Ошибка', 'error');
      });
      toast.reward({
        title: `${category.name} — план 0`,
        coin: false,
        sub: 'история цела, категория внизу списка',
      });
    }
    advance();
  }

  return (
    <CheckStep
      key={`${idx}-${currentName}`}
      category={category}
      idx={idx}
      total={N}
      onNext={handleNext}
      onSkip={handleSkip}
    />
  );
}
